import React, { useState } from 'react';
import { Scissors, Ruler, Package, Zap, Palette, Truck, Factory, IndianRupee, ChevronDown, Star, Phone, MapPin, Mail, Youtube, MessageCircle } from 'lucide-react';

const iconMap = { Scissors, Ruler, Package, Zap, Palette, Truck, Factory, IndianRupee };

export const FeaturesSection = ({ features, heroQuote, title }) => (
  <section className="px-3 md:px-6 py-8 bg-gradient-to-b from-white to-orange-50/40">
    <div className="max-w-6xl mx-auto">
      {title && <h2 className="text-xl font-bold text-neutral-900 text-center mb-2">{title}</h2>}
      {heroQuote && (
        <div className="mb-6 text-center">
          <p className="text-[15px] md:text-base italic text-neutral-700 max-w-3xl mx-auto leading-relaxed">“{heroQuote}”</p>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(features || []).map((f) => {
          const Icon = iconMap[f.icon] || Zap;
          return (
            <div key={f.id} className="p-4 bg-white rounded-lg border border-neutral-200 hover:border-orange-400 hover:shadow-md transition-all">
              <div className="w-9 h-9 rounded-md bg-orange-50 grid place-items-center mb-2">
                <Icon className="w-5 h-5 text-orange-600" />
              </div>
              <div className="font-semibold text-[13px] text-neutral-900 mb-1">{f.title}</div>
              <div className="text-[12px] text-neutral-600 leading-snug">{f.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export const StatsSection = ({ stats }) => (
  <section className="px-3 md:px-6 py-6 bg-neutral-900 text-white">
    <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
      {(stats || []).map((s) => (
        <div key={s.id} className="text-center">
          <div className="text-lg md:text-xl font-bold text-orange-400">{s.value}</div>
          <div className="text-[12px] text-neutral-300 mt-0.5">{s.label}</div>
        </div>
      ))}
    </div>
  </section>
);

export const PricingSection = ({ pricing, title }) => (
  <section id="pricing" className="px-3 md:px-6 py-8 bg-white">
    <div className="max-w-5xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-neutral-900">{title || 'Wholesale Price List'}</h2>
        <p className="text-[13px] text-neutral-600">Volume pricing on all blank apparel. GST invoice on every order.</p>
      </div>
      <div className="overflow-hidden rounded-lg border border-neutral-200">
        <table className="w-full text-[13px]">
          <thead className="bg-neutral-900 text-white">
            <tr>
              <th className="px-4 py-2.5 text-left">Product</th>
              <th className="px-4 py-2.5 text-left">Size Range</th>
              <th className="px-4 py-2.5 text-center">Bulk (≥10)</th>
              <th className="px-4 py-2.5 text-center">Sample (&lt;10)</th>
            </tr>
          </thead>
          <tbody>
            {(pricing || []).map((p, i) => (
              <tr key={p.id} className={`border-b border-neutral-100 hover:bg-orange-50/40 ${i % 2 ? 'bg-neutral-50/50' : ''}`}>
                <td className="px-4 py-2 font-medium text-neutral-900">{p.product}</td>
                <td className="px-4 py-2 text-neutral-600">{p.size}</td>
                <td className="px-4 py-2 text-center font-semibold text-emerald-700">₹{p.price_high}</td>
                <td className="px-4 py-2 text-center text-neutral-700">₹{p.price_low}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export const FAQSection = ({ faqs, title }) => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="px-3 md:px-6 py-8 bg-neutral-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-xl font-bold text-neutral-900 mb-4">{title || 'Frequently Asked Questions'}</h2>
        <div className="space-y-2">
          {(faqs || []).map((f, i) => (
            <div key={f.id} className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-neutral-50 transition-colors">
                <span className="text-[14px] font-semibold text-neutral-900">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform shrink-0 ${open === i ? 'rotate-180' : ''}`} />
              </button>
              {open === i && (
                <div className="px-4 pb-3 text-[13px] text-neutral-700 leading-relaxed border-t border-neutral-100 pt-2">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const TestimonialStrip = () => (
  <section className="px-3 md:px-6 py-4 bg-white border-y border-neutral-200">
    <div className="max-w-6xl mx-auto flex items-center justify-center gap-6 flex-wrap text-center">
      <div className="flex items-center gap-1">
        {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />)}
        <span className="ml-2 text-[13px] font-semibold">4.9/5</span>
        <span className="text-[12px] text-neutral-500">· 12,000+ printing brands trust us</span>
      </div>
    </div>
  </section>
);

export const Footer = ({ branding }) => (
  <footer className="bg-neutral-900 text-neutral-300 px-3 md:px-6 py-8">
    <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
      <div className="col-span-2 md:col-span-1">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-md bg-orange-500 text-white grid place-items-center font-bold">B</div>
          <div className="font-bold text-white">{branding?.name}</div>
        </div>
        <p className="text-[12px] leading-relaxed">{branding?.display_name} — own-factory manufacturer of plain blank apparel for printing businesses since 2017.</p>
      </div>
      <div>
        <div className="text-white font-semibold text-[13px] mb-2">Products</div>
        <ul className="space-y-1 text-[12px]">
          <li><a href="#" className="hover:text-orange-400">Oversized Tees</a></li>
          <li><a href="#" className="hover:text-orange-400">Plain Hoodies</a></li>
          <li><a href="#" className="hover:text-orange-400">Polo T-shirts</a></li>
          <li><a href="#" className="hover:text-orange-400">Sweatshirts</a></li>
        </ul>
      </div>
      <div>
        <div className="text-white font-semibold text-[13px] mb-2">Company</div>
        <ul className="space-y-1 text-[12px]">
          <li><a href="#about" className="hover:text-orange-400">About us</a></li>
          <li><a href="#faq" className="hover:text-orange-400">FAQ</a></li>
          <li><a href="#pricing" className="hover:text-orange-400">Pricing</a></li>
        </ul>
      </div>
      <div>
        <div className="text-white font-semibold text-[13px] mb-2">Contact</div>
        <ul className="space-y-1.5 text-[12px]">
          <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {branding?.contact_phone}</li>
          {branding?.contact_phone_2 && <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {branding.contact_phone_2}</li>}
          <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {branding?.contact_email}</li>
          <li className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5" /> {branding?.contact_address}</li>
          <li className="flex items-center gap-3 pt-1">
            <a href="#" className="hover:text-orange-400"><Youtube className="w-4 h-4" /></a>
            <a href="#" className="hover:text-orange-400"><MessageCircle className="w-4 h-4" /></a>
          </li>
        </ul>
      </div>
    </div>
    <div className="max-w-6xl mx-auto mt-6 pt-4 border-t border-neutral-800 text-[11px] text-neutral-500 flex items-center justify-between flex-wrap gap-2">
      <span>© 2026 {branding?.name}. All rights reserved.</span>
      <span>Manufacturing since 2017 · GST Registered</span>
    </div>
  </footer>
);
